import React, { useCallback, useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments, FilterListPopover, MaybeTooltip } from '@modernloop/shared/components';
import { CodeIcon } from '@modernloop/shared/icons';
import {
  AutocompleteRenderGroupParams,
  Box,
  Button,
  CircularProgress,
  IconButton,
  ListItemButton,
  ListSubheader,
  Tooltip,
} from '@mui/material';

import {
  CollaborationToolFilterPopover_CollaborationLinkTypeFragment,
  CollaborationToolType,
  useCollaborationToolFilterPopoverOrgIntegrationQuery,
} from 'src/generated/mloop-graphql';

import { useOrgCollaborationService } from 'src/hooks/collaborationService';

import useCollaborationToolOptions, {
  BLANK_CODING_OPTION_ID,
  NO_CODING_LINK_OPTION,
  NO_CODING_LINK_OPTION_ID,
} from './useCollaborationToolOptions';

type Props = {
  onUpdated: (
    collaborationTool:
      | {
          collaborationToolType: CollaborationToolType;
          collaborationLinkTemplateId: string;
        }[]
      | undefined
  ) => void;
};

type Fragments = {
  collaborationLinkType: CollaborationToolFilterPopover_CollaborationLinkTypeFragment | undefined;
};

const CollaborationToolFilterPopover: FCWithFragments<Fragments, Props> = ({ collaborationLinkType, onUpdated }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const collaborationToolService = useOrgCollaborationService();

  const { data: integrations } = useCollaborationToolFilterPopoverOrgIntegrationQuery();

  const integration = useMemo(() => {
    if (integrations?.thisOrg?.integrations?.coderpad) {
      return CollaborationToolType.Coderpad;
    }
    if (integrations?.thisOrg?.integrations?.codility) {
      return CollaborationToolType.Codility;
    }
    if (integrations?.thisOrg?.integrations?.codesignal) {
      return CollaborationToolType.Codesignal;
    }
    if (integrations?.thisOrg?.integrations?.hackerrank) {
      return CollaborationToolType.Hackerrank;
    }

    return undefined;
  }, [integrations]);

  const hasCodingIntegration = useMemo(() => {
    return Boolean(
      integrations?.thisOrg?.integrations?.coderpad ||
      integrations?.thisOrg?.integrations?.codility ||
      integrations?.thisOrg?.integrations?.codesignal ||
      integrations?.thisOrg?.integrations?.hackerrank
    );
  }, [integrations]);

  const { options, loading } = useCollaborationToolOptions();

  const handleUpdateCodingLink = useCallback(
    (templateId: string) => {
      if (!hasCodingIntegration) {
        return;
      }
      let input;
      if (templateId === BLANK_CODING_OPTION_ID) {
        input = [
          {
            collaborationToolType: integration,
          },
        ];
      } else if (templateId === NO_CODING_LINK_OPTION_ID) {
        input = undefined;
      } else {
        input = [
          {
            collaborationToolType: integration,
            collaborationLinkTemplateId: templateId,
          },
        ];
      }

      onUpdated(input);
    },
    [hasCodingIntegration, onUpdated, integration]
  );

  const newOptions = useMemo(() => {
    return [{ ...NO_CODING_LINK_OPTION, name: t('hiring-dashboard-common:common.select.noCodingLink') }, ...options];
  }, [options, t]);

  const selectedOption = useMemo(() => {
    return newOptions.find((option) => option.id === collaborationLinkType?.collaborationLinkTemplateId);
  }, [newOptions, collaborationLinkType]);

  const handleShowCodingLinkMenu = () => {
    // don't open the menu if there is no coding integration or templates
    if (!collaborationLinkType && !options.length) {
      return;
    }
    setOpen(true);
  };
  const renderGroup = (params: AutocompleteRenderGroupParams) => [
    params.group ? (
      <ListSubheader sx={{ alignContent: 'center' }} key={params.key}>
        {params.group}
      </ListSubheader>
    ) : null,
    params.children,
  ];

  const groupBy = (option) => {
    return option.groupName || '';
  };

  const renderOption = (props: React.HTMLAttributes<HTMLElement>, option) => {
    return (
      <ListItemButton {...props}>
        <MaybeTooltip label={option.name || ''} title={option.name || ''} />
      </ListItemButton>
    );
  };

  const getLabel = () => {
    if (loading) {
      return <CircularProgress size={16} color="primary" />;
    }

    const blankTitle = options.length
      ? t('hiring-dashboard-common:common.select.noTemplate', { tool: collaborationToolService?.name })
      : collaborationToolService?.name;

    if (collaborationLinkType?.collaborationToolType && !collaborationLinkType?.collaborationLinkTemplateId) {
      return (
        <Tooltip title={t('hiring-dashboard-common:common.select.noTemplateSet')}>
          <Button
            data-testid="job-stage-interview-coding-link-button"
            size="small"
            startIcon={
              <Box
                component="img"
                alt={collaborationToolService?.name}
                sx={{
                  height: 20,
                  width: 20,
                  borderRadius: 1,
                  boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.borderState.default}`,
                }}
                src={collaborationToolService?.logoUrl}
              />
            }
            variant="contained"
            onClick={() => {
              if (!options.length) {
                handleUpdateCodingLink(NO_CODING_LINK_OPTION_ID);
                return;
              }
              handleShowCodingLinkMenu();
            }}
          >
            <MaybeTooltip
              labelProps={{
                variant: 'inherit',
              }}
              label={blankTitle}
              title={blankTitle}
            />
          </Button>
        </Tooltip>
      );
    }
    if (!collaborationLinkType || !selectedOption) {
      return (
        <Tooltip
          title={
            hasCodingIntegration
              ? t('hiring-dashboard-common:common.select.selectCodingTool')
              : t('hiring-dashboard-common:common.select.noCodingToolIntegrated')
          }
        >
          <IconButton
            data-testid="job-stage-interview-no-coding-link-button"
            size="small"
            onClick={handleShowCodingLinkMenu}
          >
            <CodeIcon color={hasCodingIntegration ? 'action' : 'disabled'} />
          </IconButton>
        </Tooltip>
      );
    }

    return (
      <Button
        data-testid="job-stage-interview-coding-link-button"
        size="small"
        startIcon={
          <Box
            component="img"
            alt={collaborationToolService?.name}
            sx={{
              height: 20,
              width: 20,
              borderRadius: 1,
              boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.borderState.default}`,
            }}
            src={collaborationToolService?.logoUrl}
          />
        }
        variant="contained"
        sx={{
          maxWidth: '100%',
        }}
        onClick={handleShowCodingLinkMenu}
      >
        <MaybeTooltip
          labelProps={{
            variant: 'inherit',
          }}
          label={selectedOption.name}
          title={selectedOption.name}
        />
      </Button>
    );
  };

  return (
    <FilterListPopover
      open={open}
      autoFocus
      values={newOptions.filter((option) => option.id === collaborationLinkType?.collaborationLinkTemplateId)}
      renderGroup={renderGroup}
      groupBy={groupBy}
      onPopoverClose={() => {
        setOpen(false);
      }}
      placeholderText={t('hiring-dashboard-common:common.select.searchCodingTemplates')}
      options={newOptions}
      loading={loading}
      onChange={(e, value) => {
        if (value.length !== 0) {
          handleUpdateCodingLink(value[value.length - 1]?.id);
          setOpen(false);
        }
      }}
      getListHeight={() => 350}
      getChildSize={() => 36}
      onAutoCompleteClose={() => {
        setOpen(false);
      }}
      getOptionLabel={(option) => option.name || ''}
      renderOption={renderOption}
    >
      <Box
        onClick={() => {
          // add blank coding option if there is no coding link or templates on click
          if (!collaborationLinkType && !options.length) {
            handleUpdateCodingLink(BLANK_CODING_OPTION_ID);
          }
        }}
      >
        {getLabel()}
      </Box>
    </FilterListPopover>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CodeLinkTemplateSelectOrgQuery = gql`
  query CollaborationToolFilterPopoverOrgIntegration {
    thisOrg {
      integrations {
        id
        coderpad {
          apiKey
        }
        codility {
          apiKey
        }
        codesignal {
          apiKey
        }
        hackerrank {
          apiKey
        }
      }
      id
    }
  }
`;

CollaborationToolFilterPopover.fragments = {
  collaborationLinkType: gql`
    fragment CollaborationToolFilterPopover_collaborationLinkType on CollaborationLinkType {
      collaborationToolType
      collaborationLinkTemplateId
    }
  `,
};

export default CollaborationToolFilterPopover;
